import { defineStore } from 'pinia'
import { ISchemaSensor } from '@/interfaces/entities/schema_sensor'
import { getActionCableConsumer } from '@/services/actioncable.service'
import getCurrentCompanyId from '@/api/company.service'
import SensorResource from '@/api/resources/sensor'
import { useSuccessStore } from './success'
import { useErrorStore } from './error'

export const useCreateSensorStore = defineStore('create-sensor', {
    state: () => ({
        connectionStatus: 'idle' as 'idle' | 'loading' | 'success' | 'error',

        connectionPayload: {} as any,

        sensorRawPayload: null as any | null,
        suggestedSchemas: [] as ISchemaSensor[],
        selectedSchema: null as ISchemaSensor | null,

        isCreatingNewSchema: false,
        draftSchemaPayload: null as any | null,
        subscription: null as any,
        connectionTimeout: null as ReturnType<typeof setTimeout> | null,
        heartbeatInterval: null as ReturnType<typeof setInterval> | null
    }),

    actions: {

        async createSensor(params){
            try{
                const response = await SensorResource.createSensor(params)
                useSuccessStore().setSuccess(['Sensor criado com sucesso!'])
                return response
            } catch (error) {
                console.error('Erro ao criar sensor:', error)
                const apiErrors = error?.response?.data?.errors
                const messages = Array.isArray(apiErrors) ? apiErrors : (apiErrors ? [String(apiErrors)] : ['Por favor, tente novamente mais tarde.'])
                useErrorStore().setErrors('Erro ao criar sensor:', messages)
                throw error
            }
        },
        async verifyConnection(subid: string) {
            this.connectionStatus = 'loading'

            if (this.connectionTimeout) {
                clearTimeout(this.connectionTimeout)
            }

            this.connectionTimeout = setTimeout(() => {
                if (this.connectionStatus === 'loading') {
                    this.connectionStatus = 'error'
                }
            }, 60000)

            try{
                const consumer = getActionCableConsumer()

                this.subscription = consumer.subscriptions.create(
                    {
                        channel: 'SensorConnectionTestChannel',
                        subid: subid,
                        company_id: getCurrentCompanyId()
                    },
                    {
                        received: (data: any) => {
                            this.connectionPayload = data
                            this.connectionStatus = 'success'
                        },
                        connected: () => {
                            console.log('Conectado ao ActionCable, aguardando payload...')
                            // Envia keep_alive imediato para autorizar início do MQTT listener no backend.
                            // Sem isso, o backend aguarda até 25s pelo primeiro heartbeat do intervalo.
                            try { this.subscription?.perform('keep_alive', {}) } catch { /* ignore */ }
                            this.startHeartbeat()
                        },
                        disconnected: () => {
                            this.stopHeartbeat()
                            console.log('Desconectado do ActionCable')
                            // Remove a subscription do consumer para evitar que o
                            // Action Cable re-subscribe automaticamente ao reconectar.
                            if (this.subscription) {
                                this.subscription.unsubscribe()
                                this.subscription = null
                            }
                            if (this.connectionStatus !== 'success') {
                                this.connectionStatus = 'error'
                            }
                        },
                        rejected: () => {
                            this.stopHeartbeat()
                            console.log('Conexão rejeitada pelo ActionCable')
                            this.connectionStatus = 'error'
                        }
                    }
                )
            } catch (error) {
                if (this.connectionTimeout) {
                    clearTimeout(this.connectionTimeout)
                    this.connectionTimeout = null
                }

                this.connectionStatus = 'error'
            }
        },

        async disconnectSensor() {
            this.stopHeartbeat()
            if (this.subscription) {
                this.subscription.unsubscribe()
                this.subscription = null
                console.log('Subscription do ActionCable cancelada.')
            }

            if (this.connectionTimeout) {
                clearTimeout(this.connectionTimeout)
                this.connectionTimeout = null
            }

            this.connectionStatus = 'idle'
            this.sensorRawPayload = null
        },

        startHeartbeat() {
            this.stopHeartbeat()
            const HEARTBEAT_INTERVAL_MS = 25_000
            this.heartbeatInterval = setInterval(() => {
                if (this.subscription) {
                    try {
                        this.subscription.perform('keep_alive', {})
                    } catch {
                        this.stopHeartbeat()
                    }
                }
            }, HEARTBEAT_INTERVAL_MS)
        },

        stopHeartbeat() {
            if (this.heartbeatInterval) {
                clearInterval(this.heartbeatInterval)
                this.heartbeatInterval = null
            }
        },
        async analyzePayloadForSchemas(sensorType: string) {
            try{
                this.selectedSchema = null
                this.suggestedSchemas = []
                this.isCreatingNewSchema = false

                const payload = this.connectionPayload 

                const body = { sensor_type: sensorType, payload: payload }

                const response = await SensorResource.assignSchemas(body)

                this.suggestedSchemas = response || []

                if (this.suggestedSchemas.length === 0) {
                    this.selectedSchema = null
                    this.startCreatingSchema();
                }
            } catch (error) {
                useErrorStore().setErrors('error', error.response?.data?.errors || ['Erro ao analisar o payload. Por favor, tente novamente.'])

                this.selectedSchema = null
                this.suggestedSchemas = []
                this.startCreatingSchema();
            }
        },

        selectSchema(schema: ISchemaSensor) {
            this.selectedSchema = schema
            this.isCreatingNewSchema = false
        },

        startCreatingSchema() {
            this.isCreatingNewSchema = true
            this.selectedSchema = null
            this.draftSchemaPayload = null
        },

        updateDraftSchema(payload: any) {
            this.draftSchemaPayload = payload
        },

        cancelCreatingSchema() {
            this.isCreatingNewSchema = false
            this.draftSchemaPayload = null
            if (this.suggestedSchemas.length === 1) {
                this.selectedSchema = this.suggestedSchemas[0]
            } else {
                this.selectedSchema = null
            }
        },

        resetState() {
            // Garante limpeza da subscription antes de resetar o estado
            this.disconnectSensor()
            this.connectionStatus = 'idle'
            this.suggestedSchemas = []
            this.selectedSchema = null
            this.isCreatingNewSchema = false
            this.draftSchemaPayload = null
            this.sensorRawPayload = null
        }
    }
})